import { useMemo } from "react";
import { cn } from "../../utilities/helpers";
import { useCurrency } from "../dropdown";
import { useGoalData } from "../goals/useGoalData";
import { buildSavingPace } from "./analytics";
import { formatCurrency, useInvestmentsQuery } from "./shared";

const WINDOW_MONTHS = 12;
const WHOLE = { maximumFractionDigits: 0 };

// Chart geometry in pixels. The gains area is fixed and the shortfall area is
// sized from the data, so months above and below the line share one scale.
const POSITIVE_AREA = 96;
const MIN_SHORTFALL_AREA = 8;
// Headroom above the bars for the required-pace label, which hangs over the
// line it marks and would otherwise clip out of the card when the requirement
// is the tallest thing on the chart.
const CHART_TOP_PAD = 16;
const BASELINE_TOP = CHART_TOP_PAD + POSITIVE_AREA;

// A month that is still filling gets a hatch rather than a solid fill: three
// days into September must not read as saving having stopped.
const PARTIAL_FILL = {
	gain: "repeating-linear-gradient(45deg, #10b981 0 3px, transparent 3px 6px)",
	loss: "repeating-linear-gradient(45deg, #f43f5e 0 3px, transparent 3px 6px)",
};

// Falling behind is a nudge to save more, not a failure state, so it never
// borrows the rose used for losing money.
const STATUS_TONE = {
	AHEAD: "bg-emerald-50 text-emerald-700",
	ON_TRACK: "bg-emerald-50 text-emerald-700",
	BEHIND: "bg-amber-50 text-amber-700",
};

const STATUS_LABEL = {
	AHEAD: "Ahead of pace",
	ON_TRACK: "On pace",
	BEHIND: "Behind pace",
};

const formatShortDate = (date) =>
	date.toLocaleDateString("en-GB", {
		day: "numeric",
		month: "short",
		year: "numeric",
		timeZone: "UTC",
	});

const describeGoal = (row) =>
	row?.goal?.type === "FINAL" ? `final ${row.goal.year} goal` : `${row?.goal?.year} goal`;

const MonthLabel = ({ month }) => (
	<span className="min-w-0 flex-1 text-center text-[10px] tabular-nums text-slate-400">
		<span className="sm:hidden">{month.charAt(0)}</span>
		<span className="hidden sm:inline">{month}</span>
	</span>
);

const SavingPace = () => {
	const { currency } = useCurrency();
	const { data: investments, isLoading } = useInvestmentsQuery();
	const {
		currentYearGoal,
		nextYearGoal,
		finalGoal,
		isLoading: isLoadingGoals,
	} = useGoalData();

	// Nearest live deadline first: that is the one a change of monthly pace can
	// still reach. An expired goal is skipped because its "requirement" is a lump
	// sum for a date already gone, which no monthly figure can be measured against.
	//
	// The requirement read off the row is the fair-estimate one, which is the
	// point: a pace line drawn from an exceptional past return would tell you to
	// save less precisely because the good years were good.
	const goal = useMemo(
		() =>
			[currentYearGoal, nextYearGoal, finalGoal].find(
				(row) => row && row.status !== "EXPIRED"
			) || null,
		[currentYearGoal, finalGoal, nextYearGoal]
	);

	const pace = useMemo(
		() =>
			buildSavingPace({
				investments,
				requiredMonthly: goal?.requiredMonthly || 0,
				months: WINDOW_MONTHS,
			}),
		[goal, investments]
	);

	const chart = useMemo(() => {
		const maxPositive = pace.months.reduce((max, month) => Math.max(max, month.net), 0);
		const maxNegative = pace.months.reduce((max, month) => Math.max(max, -month.net), 0);
		// The required-pace line shares the axis, so a pace far under the
		// requirement has to shrink the bars rather than push the line off the top.
		// The trailing 1 keeps an all-empty window from dividing by zero.
		const scale = Math.max(maxPositive, maxNegative, pace.requiredMonthly, 1);

		return {
			scale,
			shortfallArea: maxNegative
				? Math.max(MIN_SHORTFALL_AREA, (maxNegative / scale) * POSITIVE_AREA)
				: 0,
			requiredOffset: (pace.requiredMonthly / scale) * POSITIVE_AREA,
		};
	}, [pace]);

	const hasRequirement = pace.requiredMonthly > 0;
	const money = (value) => formatCurrency(value, currency, WHOLE);

	const subtitle = !goal
		? "No goal set, so there is nothing to measure the pace against."
		: hasRequirement
		? `Net new money each month, against your ${describeGoal(goal)}.`
		: `Your ${describeGoal(goal)} needs no monthly investment at the fair growth estimate, so there is no required pace to draw.`;

	// Goals decide the whole framing of this card, so the skeleton waits for them
	// too rather than flashing "no goal set" and then correcting itself.
	const showSkeleton = isLoadingGoals || (isLoading && !investments);

	return (
		<section className="rounded-xl border border-slate-200 bg-white shadow-sm">
			<div className="p-4 pb-3 md:px-6 md:pt-5">
				<h2 className="text-lg font-semibold text-slate-900">Saving pace</h2>
				<p className="text-sm text-slate-500">{subtitle}</p>
			</div>

			<div className="px-4 pb-5 md:px-6">
				{showSkeleton && (
					<div className="space-y-3" aria-hidden="true">
						<div className="h-9 animate-pulse rounded-lg bg-slate-100" />
						<div className="h-20 animate-pulse rounded bg-slate-100" />
					</div>
				)}

				{!showSkeleton && !investments?.length && (
					<p className="py-6 text-center text-sm text-slate-500">
						No investments recorded yet.
					</p>
				)}

				{!showSkeleton && investments?.length > 0 && (
					<>
						<p
							className={cn(
								"mb-5 rounded-lg px-3 py-2 text-sm",
								hasRequirement
									? STATUS_TONE[pace.status] || "bg-slate-50 text-slate-600"
									: "bg-slate-50 text-slate-600"
							)}
						>
							<span className="font-medium">
								{(hasRequirement && STATUS_LABEL[pace.status]) || "Current pace"}
							</span>{" "}
							— you put in{" "}
							<span className="font-semibold tabular-nums">
								{money(pace.averageMonthlyNet)}
							</span>{" "}
							a month on average
							{hasRequirement && (
								<>
									{" "}
									against the{" "}
									<span className="font-semibold tabular-nums">
										{money(pace.requiredMonthly)}
									</span>{" "}
									a month your {describeGoal(goal)} needs
								</>
							)}
							.
						</p>

						<div
							aria-hidden="true"
							className="relative"
							style={{ paddingTop: CHART_TOP_PAD }}
						>
							<div
								className="flex items-stretch gap-1"
								style={{ height: POSITIVE_AREA + chart.shortfallArea }}
							>
								{pace.months.map((month) => {
									const isLoss = month.net < 0;
									const height = (Math.abs(month.net) / chart.scale) * POSITIVE_AREA;

									return (
										<div
											key={month.key}
											className="flex min-w-0 flex-1 flex-col"
											title={`${month.label}: ${formatCurrency(month.net, currency)} net${
												month.isPartial ? " so far — month still in progress" : ""
											}`}
										>
											{/* The slot is drawn whether or not money went in: a month with
											    no buys has to read as an empty column, not a missing one. */}
											<div
												className="flex items-end bg-slate-50"
												style={{ height: POSITIVE_AREA }}
											>
												{!isLoss && height > 0 && (
													<div
														className={cn(
															"w-full rounded-t-sm",
															month.isPartial ? "bg-emerald-100" : "bg-emerald-500"
														)}
														style={{
															height,
															backgroundImage: month.isPartial
																? PARTIAL_FILL.gain
																: undefined,
														}}
													/>
												)}
											</div>
											{chart.shortfallArea > 0 && (
												<div
													className="flex items-start"
													style={{ height: chart.shortfallArea }}
												>
													{isLoss && (
														<div
															className={cn(
																"w-full rounded-b-sm",
																month.isPartial ? "bg-rose-100" : "bg-rose-500"
															)}
															style={{
																height,
																backgroundImage: month.isPartial
																	? PARTIAL_FILL.loss
																	: undefined,
															}}
														/>
													)}
												</div>
											)}
										</div>
									);
								})}
							</div>

							<span
								className="absolute inset-x-0 h-px bg-slate-300"
								style={{ top: BASELINE_TOP }}
							/>

							{hasRequirement && (
								<>
									<span
										className="absolute inset-x-0 border-t border-dashed border-amber-400"
										style={{ top: BASELINE_TOP - chart.requiredOffset }}
									/>
									<span
										className="absolute right-0 -translate-y-full bg-white px-1 text-[10px] font-medium tabular-nums text-amber-700"
										style={{ top: BASELINE_TOP - chart.requiredOffset }}
									>
										{money(pace.requiredMonthly)}/mo needed
									</span>
								</>
							)}

							<div className="mt-1.5 flex gap-1">
								{pace.months.map((month) => (
									<MonthLabel key={month.key} month={month.label.split(" ")[0]} />
								))}
							</div>
						</div>

						<table className="sr-only">
							<caption>
								Net new money by month over the last {WINDOW_MONTHS} months
							</caption>
							<thead>
								<tr>
									<th scope="col">Month</th>
									<th scope="col">Net contribution</th>
								</tr>
							</thead>
							<tbody>
								{pace.months.map((month) => (
									<tr key={month.key}>
										<th scope="row">
											{month.label}
											{month.isPartial ? " (still in progress)" : ""}
										</th>
										<td>{formatCurrency(month.net, currency)}</td>
									</tr>
								))}
							</tbody>
						</table>

						<p className="mt-3 text-xs text-slate-500">
							{pace.monthsWithNoBuys
								? `No new money in ${pace.monthsWithNoBuys} of the last ${WINDOW_MONTHS} months`
								: `New money in all ${WINDOW_MONTHS} months`}
							{" · "}
							{pace.lastContributionDate
								? `last buy ${formatShortDate(pace.lastContributionDate)}`
								: "no buys recorded"}
							{/* The hatch has nothing to point at in a month with no activity,
							    and the legend then reads as a missing bar. */}
							{pace.months[pace.months.length - 1]?.net !== 0 &&
								" · hatched bar is this month so far"}
						</p>
					</>
				)}
			</div>
		</section>
	);
};

export default SavingPace;
